// -------------------------------
// Enumerations
// -------------------------------

/**
 * The change types the API ACTUALLY writes, extracted from the 43
 * `createChangeLog` call sites in sound-api rather than designed here.
 *
 * The previous list was aspirational and had drifted badly: 19 of its 25
 * options matched ZERO rows in prod (A/B Test, Emergency Fix, Processor
 * Escalation, Technology Upgrade …) while 24 types that are written every day
 * — MID Update, NMI Link, Company Created, DBA Added — had no option at all.
 * 95.7% of the collection was unreachable through the Type filter.
 *
 * Those missing options were not rot, and this is the important part for
 * whoever revisits it: they described DELIBERATE OPERATIONAL WORK (an
 * escalation, an experiment), not record mutations. Only the automatic
 * CRUD-logging half of the original design was ever built. They are omitted
 * here because a filter must describe what exists; they are NOT a to-do list
 * for more `changeType` strings.
 *
 * KEEP IN SYNC WITH THE WRITERS. Nothing enforces this — there is no endpoint
 * serving the distinct set — so an added `changeType:` in sound-api needs a
 * line here or it becomes unfilterable. Rules-only types are excluded: that
 * collection is empty and the product is not live.
 */
export const ChangeTypes = {
  APPLICATION_STATUS_CHANGE: "Application Status Change",
  CHECKOUT_CHAMP_DEACTIVATION: "Checkout Champ Deactivation",
  CHECKOUT_CHAMP_PROVISIONING: "Checkout Champ Provisioning",
  CHECKOUT_CHAMP_SYNC: "Checkout Champ Sync",
  COMPANY_CREATED: "Company Created",
  CREDENTIAL_MANAGEMENT: "Credential Management",
  CREDENTIAL_REVEAL: "Credential Reveal",
  DBA_ADDED: "DBA Added",
  DBA_UPDATED: "DBA Updated",
  DISPUTIFIER_ENROLLMENT: "Disputifier Enrollment",
  DISPUTIFIER_LINK: "Disputifier Link",
  DISPUTIFIER_MID_CLOSED: "Disputifier MID Closed",
  DISPUTIFIER_MID_ENROLLMENT: "Disputifier MID Enrollment",
  DISPUTIFIER_MID_UPDATE: "Disputifier MID Update",
  DISPUTIFIER_UPDATE: "Disputifier Update",
  ENTITY_LINKED: "Entity Linked",
  ENTITY_UNLINKED: "Entity Unlinked",
  FILE_UPLOAD: "File Upload",
  MID_APPLICATION_STATUS_CHANGE: "MID Application Status Change",
  MID_CREATED: "MID Created",
  MID_INTEGRATION_CREATED: "MID Integration Created",
  MID_INTEGRATION_DEACTIVATED: "MID Integration Deactivated",
  MID_INTEGRATION_UPDATED: "MID Integration Updated",
  MID_STATUS_CHANGE: "MID Status Change",
  MID_UPDATE: "MID Update",
  NMI_DEACTIVATION: "NMI Deactivation",
  NMI_LINK: "NMI Link",
  NMI_PROVISIONING: "NMI Provisioning",
  NMI_SYNC: "NMI Sync",
  OWNER_ADDED: "Owner Added",
  OWNER_REMOVED: "Owner Removed",
  OWNER_UPDATED: "Owner Updated",
  SOURCE_ADDED: "Source Added",
  SOURCE_REMOVED: "Source Removed",
  SOURCE_UPDATED: "Source Updated",
  USER_PASSWORD_RESET: "User Password Reset",
};

// Why the change was made (the driver or context).
//
// "Planned" is a catch-all the CRM writers reach for to mean "a person did this
// on purpose" — it does NOT mean a planned routing experiment, though it used
// to be read that way, which is how creating a company earned a 30-day
// observation window. Nothing branches on it any more; it is a label.
export const ChangeCategories = {
  CRM: "CRM",
  PORTFOLIO: "Portfolio",
  PLANNED: "Planned",
  // Credential reveals (routes/credentials.mjs:550,762). Added 2026-09-29,
  // eighteen days after this list was rebuilt from the writers — by which time
  // 43 records had accumulated that the Category filter could not reach, and
  // they are the SOC2-relevant ones. That is the drift this file warns about,
  // arriving on schedule. The durable fix is the API serving its own distinct
  // values instead of a hand-maintained mirror here.
  ACCESS: "Access",
  EXTERNAL: "External",
};

// Lifecycle state of a change.
//
// Retained for the detail header only. Deliberately NOT a filter or a column
// any more: every record the system writes is a completed fact, so the field
// carried no information — and until the routing branch was fixed, 859 records
// read "Pending" forever because a fabricated observation window had not
// elapsed. It means something again only if measurement is built.
export const StatusTypes = {
  LIVE: "Live",
  TESTING: "Testing",
  ROLLBACK_REQUIRED: "Rollback",
  PENDING: "Pending",
  ARCHIVED: "Archived",
};

// Confidence level in projected revenue impact or expected outcome
export const ConfidenceLevels = {
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
};

/*
 * THE DETAIL PAGE NO LONGER BRANCHES ON ANYTHING (CPO 2026-09-11).
 *
 * It renders Change Details, Core Information and Tags for every record, full
 * stop. Gone with the branch: `hasRoutingData`, `BASE_SECTIONS`,
 * `ROUTING_SECTIONS`, and before them a `CATEGORY_SECTIONS` map keyed on
 * category — which is the one that caused the damage, because "Planned" mapped
 * to the routing set and half the CRM writes "Planned".
 *
 * The four routing surfaces it used to select between are all deleted:
 * Observation Progress, Replay Query, Revenue Impact and the trend chart. The
 * first three were fed by fields the API fabricated — across 2,867 prod records
 * `query.baseUrl` was null on every one, `expectedOutcome` empty on every one,
 * `projectedUplift` zero on every one. The chart fetched
 * `/analytics/trend-analysis`, retired in SOUND-310, so it 404'd on every
 * record it ever rendered against.
 *
 * Measuring the impact of a change is still wanted, and is being modelled
 * separately from the change log. Do NOT re-attach a measurement surface to an
 * audit record: an audit record is an immutable statement that something
 * happened, and a measurement evolves for weeks after. That mismatch is what
 * produced a company-creation event with a 30-day observation window.
 *
 * The API-side `hasRoutingData` in changeLogService STAYS — it is what stops
 * these fields being invented in the first place.
 */

/**
 * Category → badge tone. Lives here rather than on a page because three
 * surfaces render it (the list, the company Activity tab, and previously the
 * card) and all three had their own copy, which is how they drifted.
 */
export function categoryBadgeClass(category) {
  switch (category) {
    case "CRM":
      return "badge-info";
    case "Portfolio":
      return "badge-brand";
    case "Planned":
      return "badge-success";
    case "Access":
      return "badge-warning";
    case "External":
      return "badge-muted";
    default:
      return "badge-muted";
  }
}
